import type React from "react";
import { type FC } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link as RouterLink } from "react-router-dom";
import { Link, Box, IconButton, Stack, Tooltip } from "@mui/material";
import { DateTime } from "luxon";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { DataGridPro, type GridColDef } from "@mui/x-data-grid-pro";
import { Modal } from "@packsize/components";

import { type OrderAudit, type OrderAuditEvent } from "@/schemas";

import { parseIsoMillis } from "../utils/date-utils";

import { MetricCard } from "./metric-card";

interface OrderDetailsModalProps {
	readonly orderAudit: OrderAudit | null;
	readonly onClose: () => void;
	readonly timeZone: string;
}

const formatDuration = (startIso?: string, endIso?: string): string => {
	if (!startIso || !endIso) {
		return "N/A";
	}

	const start = DateTime.fromISO(startIso);
	const end = DateTime.fromISO(endIso);

	if (!start.isValid || !end.isValid) {
		return "N/A";
	}

	const diffMs = end.toMillis() - start.toMillis();

	if (diffMs <= 0) {
		return "N/A";
	}

	const totalSeconds = Math.floor(diffMs / 1000);
	const hours = Math.floor(totalSeconds / 3600);
	const minutes = Math.floor((totalSeconds % 3600) / 60);
	const seconds = totalSeconds % 60;

	if (hours > 0) {
		return `${hours}h ${minutes}m ${seconds}s`;
	}

	if (minutes > 0) {
		return `${minutes}m ${seconds}s`;
	}

	return `${seconds}s`;
};

const MetricLabelWithInfo: FC<{ readonly label: React.ReactNode; readonly tooltip: React.ReactNode }> = ({
	label,
	tooltip,
}) => (
	<Stack direction="row" gap={1} alignItems="center">
		{label}
		<Tooltip title={tooltip} arrow placement="top">
			<IconButton size="small" sx={{ color: "text.secondary" }}>
				<InfoOutlinedIcon fontSize="small" />
			</IconButton>
		</Tooltip>
	</Stack>
);

export const OrderDetailsModal: FC<OrderDetailsModalProps> = ({ orderAudit, onClose, timeZone }) => {
	const intl = useIntl();

	const handleClose = (event?: React.UIEvent) => {
		event?.stopPropagation();
		onClose();
	};

	if (!orderAudit) {
		return null;
	}

	const sortedEvents = [...orderAudit.events].sort((a, b) => {
		const aMillis = parseIsoMillis(a.eventDate);
		const bMillis = parseIsoMillis(b.eventDate);

		if (aMillis === null && bMillis === null) return 0;
		if (aMillis === null) return 1;
		if (bMillis === null) return -1;

		return aMillis - bMillis;
	});

	const eventCount = sortedEvents.length;
	const firstEventDate = orderAudit.firstEventDate;
	const lastEventDate = orderAudit.lastEventDate;

	const boxCreateReceived = sortedEvents.find((event) => event.eventType === "BOX_CREATE_RECEIVED");
	const boxCreate = sortedEvents.find((event) => event.eventType === "BOX_CREATE");
	const boxProduced = sortedEvents.find((event) => event.eventType === "BOX_PRODUCED");

	const queueTime = formatDuration(boxCreateReceived?.eventDate, boxCreate?.eventDate);
	const timeToProduce = formatDuration(boxCreate?.eventDate, boxProduced?.eventDate);
	const totalDuration = formatDuration(firstEventDate, lastEventDate);

	const rows = orderAudit.events.map((event, index) => ({
		id: `${event.eventDate}-${event.correlationId}-${event.jobId ?? index}`,
		...event,
	}));

	const columns: GridColDef<OrderAuditEvent>[] = [
		{
			field: "eventDate",
			headerName: intl.formatMessage({ id: "orders_audit.event_date", defaultMessage: "Event Date" }),
			minWidth: 200,
			flex: 1,
			valueGetter: (_, event) => {
				const millis = parseIsoMillis(event.eventDate);
				return millis ?? Number.MAX_SAFE_INTEGER;
			},
			renderCell: (params) => {
				const dt = DateTime.fromISO(params.row.eventDate);
				if (!dt.isValid) {
					return "N/A";
				}
				return dt.setZone(timeZone).toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS);
			},
		},
		{
			field: "eventType",
			headerName: intl.formatMessage({ id: "orders_audit.event_type", defaultMessage: "Event Type" }),
			minWidth: 160,
			flex: 1,
		},
		{
			field: "correlationId",
			headerName: intl.formatMessage({ id: "orders_audit.correlation_id", defaultMessage: "Correlation Id" }),
			minWidth: 220,
			flex: 1.5,
		},
		{
			field: "jobDetails",
			headerName: intl.formatMessage({ id: "orders_audit.job_details", defaultMessage: "Job Details" }),
			sortable: false,
			minWidth: 140,
			flex: 0.7,
			align: "right",
			headerAlign: "right",
			renderCell: (params) =>
				params.row.jobId ? (
					<Link component={RouterLink} to={`/dashboard/packaging-solution-audit-trail/${params.row.jobId}`}>
						<FormattedMessage id="orders_audit.view" defaultMessage="View" />
					</Link>
				) : null,
		},
	];

	return (
		<Modal
			open={Boolean(orderAudit)}
			onClose={handleClose}
			title={intl.formatMessage({ id: "orders_audit.order_events_history", defaultMessage: "Order Events History" })}
			size="lg"
		>
			<Stack spacing={2}>
				<Stack direction="row" justifyContent="stretch" spacing={2} flexWrap="wrap">
					<MetricCard
						label={<FormattedMessage id="orders_audit.order_id" defaultMessage="Order Id" />}
						value={orderAudit.orderId}
						variant="info"
						data-testid="order-id-card"
					/>
					<MetricCard
						label={<FormattedMessage id="orders_audit.pick_ticket_id" defaultMessage="Pick Ticket Id" />}
						value={orderAudit.pickTicketId}
						variant="info"
						data-testid="pick-ticket-id-card"
					/>
					<MetricCard
						label={<FormattedMessage id="orders_audit.events" defaultMessage="Events" />}
						value={eventCount}
						variant="info"
						minWidth={180}
						data-testid="events-count-card"
					/>
				</Stack>

				<Stack direction="row" justifyContent="center" spacing={2} flexWrap="wrap" sx={{ mt: 2 }}>
					<MetricCard
						label={
							<MetricLabelWithInfo
								label={<FormattedMessage id="orders_audit.queue_time" defaultMessage="Queue Time" />}
								tooltip={
									<FormattedMessage
										id="orders_audit.queue_time_help"
										defaultMessage="Time between order received and production started (BOX_CREATE_RECEIVED → BOX_CREATE)"
									/>
								}
							/>
						}
						value={queueTime}
						variant="metric"
						data-testid="queue-time-card"
					/>
					<MetricCard
						label={
							<MetricLabelWithInfo
								label={<FormattedMessage id="orders_audit.time_to_produce" defaultMessage="Time to Produce" />}
								tooltip={
									<FormattedMessage
										id="orders_audit.time_to_produce_help"
										defaultMessage="Time between production started and box produced (BOX_CREATE → BOX_PRODUCED)"
									/>
								}
							/>
						}
						value={timeToProduce}
						variant="metric"
						data-testid="time-to-produce-card"
					/>
					<MetricCard
						label={
							<MetricLabelWithInfo
								label={
									<FormattedMessage id="orders_audit.total_order_duration" defaultMessage="Total Order Duration" />
								}
								tooltip={
									<FormattedMessage
										id="orders_audit.total_order_duration_help"
										defaultMessage="Total time from first event to last event in the order lifecycle"
									/>
								}
							/>
						}
						value={totalDuration}
						variant="metric"
						data-testid="total-duration-card"
					/>
				</Stack>

				<Box sx={{ mt: 2 }}>
					<DataGridPro
						autoHeight
						rows={rows}
						columns={columns}
						pagination
						pageSizeOptions={[10, 25, 50]}
						initialState={{
							pagination: {
								paginationModel: { pageSize: 10, page: 0 },
							},
							sorting: {
								sortModel: [{ field: "eventDate", sort: "desc" }],
							},
						}}
						disableColumnReorder
						disableColumnMenu
						disableColumnSelector
						disableRowSelectionOnClick
					/>
				</Box>
			</Stack>
		</Modal>
	);
};
